import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft } from "react-icons/lu";
import style from "./UserDetails.module.css";
import { userDetail } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

// ---- formatting helpers -----------------------------------------------------
const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const fmtMoney = (amount, currency = "USD") => {
  if (amount === null || amount === undefined) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
    }).format(amount);
  } catch {
    return `$${Number(amount).toFixed(2)}`;
  }
};

// Maps a raw Subscription.status value (SubscribersController.detail() sends
// this column verbatim, no transform) to the CSS class that visually
// reflects it, instead of always showing the "active/healthy" green style
// regardless of actual status (e.g. a refunded/past_due sub used to render
// identically to an active one).
const subStatusClass = (status, style) => {
  switch (status) {
    case "active":
    case "trialing":
      return style.subValActive;
    case "cancelled":
    case "expired":
    case "refunded":
    case "past_due":
    case "payment_failed":
      return style.subValDanger;
    // "inactive" (e.g. a superseded free row) / "deferred" / anything
    // unrecognized: neutral, not styled as healthy or as a problem.
    default:
      return style.subVal;
  }
};

// Same idea for the Billing History table. Per SubscribersController.detail(),
// row.status is one of: "Upcoming" (synthetic, next scheduled charge), "Comp"
// (admin-granted, no money), "Free" (free-tier row), "Paid" (a real charge),
// "Unknown" (missing status), or — for any non-admin/non-free row whose raw
// status isn't 'active' — the raw Subscription.status value passed straight
// through (cancelled/expired/refunded/past_due/payment_failed/inactive/etc).
const billingStatusClass = (status, style) => {
  switch (status) {
    case "Paid":
      return style.statusPaid;
    case "Upcoming":
      return style.statusUpcoming;
    case "Comp":
    case "Free":
      return style.statusNeutral;
    case "cancelled":
    case "expired":
    case "refunded":
    case "past_due":
    case "payment_failed":
      return style.statusDanger;
    default:
      return style.deviceStatus;
  }
};

// Watch time: show minutes under an hour (so 6 min reads "6m", not "0h"),
// hours (one decimal) under a day, then whole hours.
const fmtWatchTime = (seconds = 0) => {
  const s = Number(seconds) || 0;
  if (s < 60) return "0m";
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  const hours = s / 3600;
  if (hours < 10) return `${(Math.round(hours * 10) / 10).toString()}h`;
  return `${Math.round(hours)}h`;
};

// "2 hours ago" style relative time
const fmtRelative = (d) => {
  if (!d) return "—";
  const then = new Date(d).getTime();
  if (isNaN(then)) return "—";
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  return fmtDate(d);
};

// Pick a device icon/label from device_type + user agent
const deviceIconFor = (type = "", ua = "") => {
  const t = `${type} ${ua}`.toLowerCase();
  if (/tv|tizen|roku|tvos|android tv|smarttv/.test(t)) return "TV";
  if (/ipad|tablet/.test(t)) return "▦";
  if (/iphone|android|mobile/.test(t)) return "📱";
  if (/web|mac|windows|linux|safari|chrome|firefox/.test(t)) return "💻";
  return "🖥";
};

// Friendly device name from type + user agent
const deviceNameFor = (type = "", ua = "") => {
  const t = `${type} ${ua}`.toLowerCase();
  if (/tizen|samsung/.test(t)) return "Samsung TV";
  if (/roku/.test(t)) return "Roku";
  if (/ipad/.test(t)) return "iPad";
  if (/iphone/.test(t)) return "iPhone";
  if (/macintosh|mac os/.test(t)) return "Mac";
  if (/windows/.test(t)) return "Windows PC";
  if (/android/.test(t)) return "Android device";
  return type || "Unknown device";
};

const browserFor = (ua = "") => {
  if (/safari/i.test(ua) && !/chrome/i.test(ua)) return "Safari";
  if (/chrome/i.test(ua)) return "Chrome";
  if (/firefox/i.test(ua)) return "Firefox";
  if (/edge/i.test(ua)) return "Edge";
  return "";
};

const titleCase = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

// Days-left + progress % between two dates
const periodInfo = (start, end) => {
  if (!start || !end) return { label: "—", daysLeft: null, percent: 0 };
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const now = Date.now();
  if (isNaN(s) || isNaN(e) || e <= s)
    return {
      label: `${fmtDate(start)} – ${fmtDate(end)}`,
      daysLeft: null,
      percent: 0,
    };
  const total = e - s;
  const elapsed = Math.min(Math.max(now - s, 0), total);
  const percent = Math.round((elapsed / total) * 100);
  const daysLeft = Math.max(0, Math.ceil((e - now) / 86400000));
  return {
    label: `Current period · ${fmtDate(start)} – ${fmtDate(end)}`,
    daysLeft,
    percent,
  };
};

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await userDetail(id);
        if (active) setData(res?.data?.data || null);
      } catch (err) {
        if (active) {
          setError(true);
          showToast(
            err?.response?.data?.message || "Failed to load user details",
            "error"
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className={style.page}>
        <div className={style.content}>
          <div className={style.stateMsg}>Loading user…</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={style.page}>
        <div className={style.topNav}>
          <div className={style.breadcrumb}>
            <button className={style.backBtn} onClick={() => navigate(-1)}>
              <LuArrowLeft />
            </button>
            <span className={style.breadcrumbLink} onClick={() => navigate(-1)}>
              Users
            </span>
          </div>
        </div>
        <div className={style.content}>
          <div className={style.stateMsg}>Could not load this user.</div>
        </div>
      </div>
    );
  }

  // ---- derive view-model from API data --------------------------------------
  const sub = data.subscription;
  const acct = data.account || {};
  const va = data.viewing_activity || {};
  const cw = va.continue_watching;
  const deviceList = data.devices?.list || [];
  const billing = data.billing_history || [];

  const period = sub
    ? periodInfo(sub.current_period_start, sub.current_period_end)
    : { label: "—", daysLeft: null, percent: 0 };

  // Profiles: count (+ kids tally) plus the actual profile names when present.
  const profileNames = (data.profiles || []).map((p) => p.name).filter(Boolean);
  const profilesCount = acct.profiles_count ?? data.profiles?.length ?? 0;
  const profilesSummary = `${profilesCount}${
    acct.kids_profiles ? ` · ${acct.kids_profiles} kids` : ""
  }`;

  // Free trial: show the trial length and, when known, the date it ends/ended.
  const trialEndsAt = sub?.trial_ends_at;
  const freeTrialLabel =
    acct.free_trial_days != null
      ? [
          `${acct.free_trial_days} days`,
          trialEndsAt
            ? `${acct.free_trial_ended ? "ended" : "ends"} ${fmtDate(
                trialEndsAt
              )}`
            : acct.free_trial_ended
            ? "ended"
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : "—";

  const accountRows = [
    { label: "Signed up", value: fmtDate(acct.signed_up_at) },
    { label: "Free trial", value: freeTrialLabel },
    { label: "Last active", value: fmtRelative(acct.last_active_at) },
    { label: "Location", value: acct.location?.label || "—" },
    {
      label: "Profiles",
      value:
        profileNames.length > 0
          ? `${profilesSummary} · ${profileNames.join(", ")}`
          : profilesSummary,
    },
  ];

  return (
    <div className={style.page}>
      {/* Top nav */}
      <div className={style.topNav}>
        <div className={style.breadcrumb}>
          <button className={style.backBtn} onClick={() => navigate(-1)}>
            <LuArrowLeft />
          </button>
          <span className={style.breadcrumbLink} onClick={() => navigate(-1)}>
            Users
          </span>
          <span className={style.breadcrumbSep}>/</span>
          <span className={style.breadcrumbCurrent}>{data.name}</span>
        </div>
      </div>

      <div className={style.content}>
        {/* Hero card */}
        <div className={style.heroCard}>
          <div className={style.heroLeft}>
            <div
              className={style.avatar}
              style={
                data.profile_img
                  ? {
                      backgroundImage: `url(${data.profile_img})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }
                  : undefined
              }
            >
              {!data.profile_img && initials(data.name)}
            </div>
            <div className={style.heroInfo}>
              <div className={style.heroNameRow}>
                <h1 className={style.heroName}>{data.name}</h1>
                <span className={style.badgeActive}>{data.status}</span>
                <span className={style.badgePlan}>{data.user_type}</span>
              </div>
              <div className={style.heroMeta}>
                <span>{data.email}</span>
                {data.email_verified ? (
                  <span className={style.verified}>✓ Email verified</span>
                ) : (
                  <span className={style.unverified}>Email not verified</span>
                )}
                <span className={style.userId}>ID {data.user_code}</span>
              </div>
            </div>
          </div>
          <div className={style.heroStats}>
            <div className={style.heroStat}>
              <div className={style.heroStatLabel}>LIFETIME VALUE</div>
              <div className={style.heroStatValue}>
                {fmtMoney(data.lifetime_value)}
              </div>
            </div>
            <div className={style.heroStat}>
              <div className={style.heroStatLabel}>WATCH TIME</div>
              <div className={style.heroStatValue}>
                {fmtWatchTime(va.watch_time_seconds)}
              </div>
            </div>
            <div className={style.heroStat}>
              <div className={style.heroStatLabel}>TITLES</div>
              <div className={style.heroStatValue}>{data.titles_count ?? 0}</div>
            </div>
          </div>
        </div>

        {/* Two-column middle */}
        <div className={style.twoCol}>
          {/* Subscription */}
          <div className={style.card}>
            <div className={style.cardHeader}>
              <span className={style.cardLabel}>SUBSCRIPTION</span>
            </div>
            {sub ? (
              <>
                <div className={style.subPlanRow}>
                  <div>
                    <div className={style.subPlanName}>{sub.plan_name}</div>
                    <div className={style.subPlanDesc}>
                      {[
                        sub.video_quality,
                        sub.max_screens
                          ? `${sub.max_screens} screen${
                              sub.max_screens === 1 ? "" : "s"
                            }`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                  <div className={style.subPrice}>
                    <span className={style.subPriceAmt}>
                      {fmtMoney(sub.amount, sub.currency)}
                    </span>
                    <span className={style.subPricePer}>
                      per {sub.billing_cycle || "month"}
                    </span>
                  </div>
                </div>
                {period.daysLeft != null && (
                  <div className={style.progressBox}>
                    <div className={style.progressLabel}>
                      <span>{period.label}</span>
                      <span>{period.daysLeft} days left</span>
                    </div>
                    <div className={style.progressTrack}>
                      <div
                        className={style.progressFill}
                        style={{ width: `${period.percent}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className={style.subGrid}>
                  <div className={style.subRow}>
                    <span className={style.subKey}>Status</span>
                    <span className={subStatusClass(sub.status, style)}>
                      {titleCase(sub.status)}
                    </span>
                  </div>
                  <div className={style.subRow}>
                    <span className={style.subKey}>Billing cycle</span>
                    <span className={style.subVal}>
                      {sub.billing_label || "—"}
                    </span>
                  </div>
                  <div className={style.subRow}>
                    <span className={style.subKey}>Subscribed</span>
                    <span className={style.subVal}>
                      {fmtDate(sub.subscribed_at)}
                    </span>
                  </div>
                  <div className={style.subRow}>
                    <span className={style.subKey}>Next renewal</span>
                    <span className={style.subVal}>
                      {sub.next_renewal ? fmtDate(sub.next_renewal) : "—"}
                    </span>
                  </div>
                  <div className={style.subRow}>
                    <span className={style.subKey}>First plan</span>
                    <span className={style.subVal}>
                      {data.first_plan || "—"}
                    </span>
                  </div>
                  <div className={style.subRow}>
                    <span className={style.subKey}>Auto-renew</span>
                    <span className={style.subVal}>
                      {sub.auto_renew ? "On" : "Off"}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <div className={style.stateMsg}>No active subscription</div>
            )}
          </div>

          {/* Account */}
          <div className={style.card}>
            <div className={style.cardHeader}>
              <span className={style.cardLabel}>ACCOUNT</span>
            </div>
            <div className={style.accountGrid}>
              {accountRows.map((row, i) => (
                <div key={i} className={style.accountRow}>
                  <span className={style.accountKey}>{row.label}</span>
                  <span className={style.accountVal}>{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Two-column bottom section */}
        <div className={style.twoCol}>
          {/* Viewing Activity */}
          <div className={style.card}>
            <div className={style.cardHeader}>
              <span className={style.cardLabel}>VIEWING ACTIVITY</span>
            </div>
            <div className={style.activityStats}>
              {[
                { val: fmtWatchTime(va.watch_time_seconds), label: "Watch time" },
                { val: va.titles_count ?? 0, label: "Titles" },
                {
                  val: `${va.avg_session_minutes ?? 0}m`,
                  label: "Avg session",
                },
                { val: va.watchlist_count ?? 0, label: "Watchlist" },
              ].map((s, i) => (
                <div key={i} className={style.activityStat}>
                  <div className={style.activityStatVal}>{s.val}</div>
                  <div className={style.activityStatLabel}>{s.label}</div>
                </div>
              ))}
            </div>
            {cw ? (
              <div className={style.continueWatching}>
                <div className={style.cwLabel}>CONTINUE WATCHING</div>
                <div className={style.cwRow}>
                  <div
                    className={style.cwThumb}
                    style={
                      cw.image
                        ? {
                            backgroundImage: `url(${cw.image})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : undefined
                    }
                  >
                    <span className={style.cwPlay}>▶</span>
                  </div>
                  <div className={style.cwInfo}>
                    <div className={style.cwTitle}>{cw.title || "—"}</div>
                    <div className={style.cwSub}>
                      {[
                        cw.episode_number ? `E${cw.episode_number}` : null,
                        cw.content_type,
                        cw.last_watched_at
                          ? fmtRelative(cw.last_watched_at)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </div>
                </div>
                {va.top_genres?.length > 0 && (
                  <div className={style.genreTags}>
                    {va.top_genres.map((g, i) => (
                      <span key={i} className={style.genreTag}>
                        {g}
                      </span>
                    ))}
                    <span className={style.genreMore}>top genres</span>
                  </div>
                )}
              </div>
            ) : (
              <div className={style.stateMsg}>No recent activity</div>
            )}
          </div>

          {/* Devices */}
          <div className={style.card}>
            <div className={style.cardHeader}>
              <span className={style.cardLabel}>DEVICES</span>
              {deviceList.length > 0 && (
                <span className={style.cardHeaderRight}>
                  {deviceList.length}{" "}
                  {deviceList.length === 1 ? "device" : "devices"}
                </span>
              )}
            </div>
            {deviceList.length > 0 ? (
              <div className={style.deviceList}>
                {deviceList.map((d, i) => {
                  const browser = browserFor(d.user_agent);
                  return (
                    <div key={i} className={style.deviceRow}>
                      <div className={style.deviceIcon}>
                        {deviceIconFor(d.device_type, d.user_agent)}
                      </div>
                      <div className={style.deviceInfo}>
                        <div className={style.deviceName}>
                          {deviceNameFor(d.device_type, d.user_agent)}
                        </div>
                        <div className={style.deviceSub}>
                          {[titleCase(d.device_type), browser]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      </div>
                      <span
                        className={
                          d.is_active_now
                            ? style.deviceStatusActive
                            : style.deviceStatus
                        }
                      >
                        {d.is_active_now
                          ? "Active now"
                          : fmtRelative(d.last_seen_at)}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={style.stateMsg}>No devices</div>
            )}
          </div>
        </div>

        {/* Billing History */}
        <div className={style.card}>
          <div className={style.cardHeader}>
            <span className={style.cardLabel}>BILLING HISTORY</span>
          </div>
          {billing.length > 0 ? (
            <div className={style.billingScroll}>
            <table className={style.billingTable}>
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>DESCRIPTION</th>
                  <th>AMOUNT</th>
                  <th>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {billing.map((row, i) => (
                  <tr key={i}>
                    <td>{fmtDate(row.date)}</td>
                    <td>{row.description}</td>
                    <td>{fmtMoney(row.amount, row.currency)}</td>
                    <td>
                      <span className={billingStatusClass(row.status, style)}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          ) : (
            <div className={style.stateMsg}>No billing history</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;
